export const ATTORNEY_CONTACT_TYPE_OPTIONS = [
  "Primary",
  "Cell",
  "Other",
  "Paralegal/Assistant",
  "Secondary",
  "System Emails",
];

export function matchKnownContactType(
  value,
  knownTypes = ATTORNEY_CONTACT_TYPE_OPTIONS
) {
  if (!value) {
    return "";
  }
  const normalized = String(value).trim().toLowerCase();
  for (let i = 0; i < knownTypes.length; i++) {
    if (knownTypes[i].toLowerCase() === normalized) {
      return knownTypes[i];
    }
  }
  if (normalized === "primary") {
    return "Primary";
  }
  if (normalized === "secondary") {
    return "Secondary";
  }
  return "";
}

export function getContactType(contact) {
  if (!contact) {
    return "";
  }
  const raw = contact.type;
  if (typeof raw === "object" && raw !== null) {
    const name = (raw.name || "").trim();
    return matchKnownContactType(name) || name;
  }
  const rawString = String(raw || "").trim();
  const matched = matchKnownContactType(rawString);
  if (matched) {
    return matched;
  }
  if (rawString.length === 1) {
    const letterMap = {
      P: "Primary",
    };
    const mapped = letterMap[rawString.toUpperCase()];
    if (mapped) {
      return mapped;
    }
  }
  return (
    rawString ||
    matchKnownContactType(contact.contact_category) ||
    matchKnownContactType(contact.contact_catagory)
  );
}

export function getContactDisplayLabel(contact, index = 0) {
  let type = getContactType(contact);
  if (!type || type.length === 1) {
    const fromNote = matchKnownContactType(contact?.note);
    if (fromNote) {
      type = fromNote;
    }
  }
  if (!type) {
    return index === 0 ? "Primary Contact" : `Contact ${index + 1}`;
  }
  return type;
}

export function getContactCardTitle(contact, index = 0) {
  const type = getContactType(contact);
  return type ? `${type} contact` : `Contact ${index + 1}`;
}

export function isPrimaryContact(contact) {
  return getContactType(contact) === "Primary";
}

export function sortContactsPrimaryFirst(contacts) {
  const list = (contacts || []).map((contact, index) => ({ contact, index }));
  list.sort((a, b) => {
    const aPrimary = isPrimaryContact(a.contact);
    const bPrimary = isPrimaryContact(b.contact);
    if (aPrimary !== bPrimary) {
      return aPrimary ? -1 : 1;
    }
    return a.index - b.index;
  });
  return list.map((entry) => entry.contact);
}

export function getContactCategorySelectClasses(contact, contacts, index) {
  const type = getContactType(contact) || contact?.type || "";
  const primaryTaken = isAttorneyPrimaryCategoryTaken(contacts, index);
  return {
    "contact-category-select--primary-blocked":
      primaryTaken && type !== "Primary",
    "contact-category--primary": type === "Primary",
    "contact-category--cell": type === "Cell",
    "contact-category--other": type === "Other",
    "contact-category--paralegal": type === "Paralegal/Assistant",
    "contact-category--secondary": type === "Secondary",
    "contact-category--system-emails": type === "System Emails",
    "contact-category--empty": !type,
  };
}

export function isPrimaryContactEmailLocked(contact) {
  if (getContactType(contact) !== "Primary") {
    return false;
  }
  return Boolean(String(contact?.old_email || "").trim());
}

export function stampPrimaryContactOldEmails(contacts) {
  (contacts || []).forEach((contact) => {
    if (getContactType(contact) === "Primary" && contact?.email) {
      contact.old_email = contact.email;
    }
  });
}

export function normalizeContact(contact) {
  if (!contact) {
    return contact;
  }
  const normalized = { ...contact };
  const note = (normalized.note || "").trim();
  const legacyCategory = (
    normalized.contact_category ||
    normalized.contact_catagory ||
    ""
  ).trim();

  if (typeof normalized.type === "object" && normalized.type !== null) {
    normalized.type = (normalized.type.name || "").trim();
  }

  let currentType = ((normalized.type || "") + "").trim();
  const matchedCategory =
    matchKnownContactType(legacyCategory) ||
    matchKnownContactType(currentType);

  if (legacyCategory) {
    normalized.type =
      matchKnownContactType(legacyCategory) || legacyCategory;
    if (!note && currentType && !matchKnownContactType(currentType)) {
      normalized.note = currentType;
    }
  } else if (matchedCategory) {
    normalized.type = matchedCategory;
  } else if (currentType && currentType.length > 1) {
    normalized.note = note || currentType;
    normalized.type = "";
  } else if (currentType) {
    normalized.type = currentType;
  } else {
    normalized.type = "";
  }

  normalized.note = normalized.note || note || "";
  delete normalized.contact_category;
  delete normalized.contact_catagory;

  if (normalized.phone) {
    normalized.phone = normalized.phone.replace(/\s+/g, "");
  }
  if (normalized.cell) {
    normalized.cell = normalized.cell.replace(/\s+/g, "");
  }

  return normalized;
}

export function normalizeContactsForForm(contacts) {
  const normalized = (contacts || []).map((contact) =>
    normalizeContact({ ...contact })
  );
  return sortContactsPrimaryFirst(normalized);
}

export function isAttorneyPrimaryCategoryTaken(contacts, excludeIndex) {
  if (!contacts || !contacts.length) {
    return false;
  }
  for (let i = 0; i < contacts.length; i++) {
    if (i !== excludeIndex && getContactType(contacts[i]) === "Primary") {
      return true;
    }
  }
  return false;
}

export function onAttorneyContactCategoryChange(contacts, contact, index) {
  if (
    getContactType(contact) === "Primary" &&
    isAttorneyPrimaryCategoryTaken(contacts, index)
  ) {
    contact.type = "";
    return 'Contact Type "Primary" can only be used once.';
  }
  return null;
}

export function validateAttorneyContactCategories(contacts) {
  if (!contacts || !contacts.length) {
    return null;
  }
  for (let i = 0; i < contacts.length; i++) {
    const label = getContactCardTitle(contacts[i], i);
    if (!getContactType(contacts[i])) {
      return `Contact Type is required for ${label}.`;
    }
  }
  let primaryCount = 0;
  for (let j = 0; j < contacts.length; j++) {
    if (getContactType(contacts[j]) === "Primary") {
      primaryCount++;
    }
  }
  if (primaryCount > 1) {
    return 'Contact Type "Primary" can only be used once.';
  }
  return null;
}

export function isContactEmpty(contact) {
  if (!contact) {
    return true;
  }
  return (
    !getContactType(contact) &&
    !(contact.note || "").trim() &&
    !(contact.email || "").trim() &&
    !(contact.phone || "").trim() &&
    !(contact.name || "").trim()
  );
}

export function stripEmptyContacts(contacts) {
  const list = [...(contacts || [])];
  for (let i = list.length - 1; i > 0; i--) {
    if (isContactEmpty(list[i])) {
      list.splice(i, 1);
    } else {
      break;
    }
  }
  return list;
}

export function createEmptyContact(defaultType = "") {
  return {
    name: "",
    type: defaultType,
    note: "",
    email: "",
    phone: "",
    cell: "",
    ext: "",
    fax: "",
  };
}

export function prepareContactsForSave(contacts) {
  return sortContactsPrimaryFirst(stripEmptyContacts(contacts)).map((contact) => {
    const payload = { ...contact };
    payload.type = getContactType(payload) || payload.type || "";
    if (payload.phone) {
      payload.phone = payload.phone.replace(/\s+/g, "");
    }
    if (payload.cell) {
      payload.cell = payload.cell.replace(/\s+/g, "");
    }
    if (typeof payload.type === "object" && payload.type !== null) {
      payload.type = getContactType(payload);
    }
    delete payload.old_email;
    delete payload.showType;
    delete payload.contact_category;
    delete payload.contact_catagory;
    return payload;
  });
}

export function normalizeContactsForApi(contacts) {
  return prepareContactsForSave(contacts);
}

export function normalizeContactsForEmail(contacts) {
  return normalizeContactsForApi(contacts).map((contact, index) => ({
    ...contact,
    type: contact.type || (index === 0 ? "Primary" : `Contact ${index + 1}`),
    note: String(contact.note || "").trim(),
  }));
}
